import apiClient, { createApiClient } from './api';
import { config } from '../config';
import type { AxiosInstance } from 'axios';
import { isWebinarStartWithinBuffer, parseApiDateAsUtc } from './webinarTiming';
import { getFieldsOfStudy } from '../utils/fieldOfStudy';

export const CourseType = {
  OnDemand: 0,
  Webinar: 1,
  Conference: 2,
} as const;

export type CourseType = typeof CourseType[keyof typeof CourseType];

export interface CourseSpeaker {
  id: string;
  name: string;
  displayName: string;
  title?: string;
  bio?: string;
  email?: string;
  avatarUrl?: string;
  sourceSystemSpeakerId?: string;
  sourceSystemId: number;
}

export interface CourseCredit {
  id: number;
  creditHours: number;
  educationCategoryName: string;
  educationCategoryId: number;
  startTime: string;
  endTime: string;
}

export interface CourseDescriptionSection {
  descriptionType: string;
  description: string;
}

export interface CourseSyllabusItem {
  id: number;
  sourceSystemItemId: string;
  title: string;
  itemType: string;
  sequence: number;
}

export interface CourseInstance {
  id: number;
  sourceSystemInstanceId?: string;
  isActive: boolean;
  startDate: string;
  endDate?: string;
}

export interface Course {
  id: number;
  title: string;
  description?: string;
  developer: string;
  category: string;
  courseType: CourseType;
  creditHours: number;
  duration?: number;
  url?: string;
  level?: string;
  sourceSystemId?: number;
  sourceSystemCourseId?: string;
  isActive?: boolean;
  cpeIssuerPartnerId?: number;
  meetingFormat?: number;
  meetingPresentationFormat?: number;
  courseCode?: string;
  // Optional fields populated for externally-sourced courses (webinar/Docebo)
  startDate?: string;
  endDate?: string;
  upcomingInstances?: CourseInstance[];
  speakers: CourseSpeaker[];
  courseCredits: CourseCredit[];
  descriptions: CourseDescriptionSection[];
  syllabusItems?: CourseSyllabusItem[];
  createdAt: string;
  updatedAt: string;
}

export interface SearchFilters {
  searchTerm?: string;
  category?: string;
  provider?: string;
  courseType?: number;
  startDate?: string;
  endDate?: string;
}

export function getEligibleWebinarInstances(
  instances?: CourseInstance[],
  from?: Date | null,
  to?: Date | null
): CourseInstance[] {
  return (instances ?? []).filter((instance) => {
    if (!isWebinarStartWithinBuffer(instance.startDate)) return false;
    if (!from && !to) return true;
    const start = parseApiDateAsUtc(instance.startDate);
    if (from && start < from) return false;
    if (to && start > to) return false;
    return true;
  });
}

export function getPreferredWebinarInstanceId(
  instances?: CourseInstance[],
  requestedInstanceId?: number | null
): number | null {
  const eligibleInstances = getEligibleWebinarInstances(instances);
  if (eligibleInstances.length === 0) return null;
  if (requestedInstanceId == null) return eligibleInstances[0].id;
  return eligibleInstances.find((instance) => instance.id === requestedInstanceId)?.id ?? eligibleInstances[0].id;
}

const CACHE_TTL_MS = 5 * 60 * 1000;

// In-memory cache for DB/Meilisearch courses (5 minute TTL)
let dbCoursesCache: Course[] | null = null;
let dbCacheTimestamp = 0;

class CourseService {
  private api: AxiosInstance;

  constructor() {
    this.api = createApiClient(config.api.courses);
  }

  /**
   * Get all courses from the main API
   */
  async getCourses(): Promise<Course[]> {
    const now = Date.now();
    if (dbCoursesCache && now - dbCacheTimestamp < CACHE_TTL_MS) {
      return dbCoursesCache;
    }
    const r = await apiClient.get('/api/courses');
    dbCoursesCache = r.data as Course[];
    dbCacheTimestamp = Date.now();
    return dbCoursesCache;
  }

  /**
   * Search courses with filters
   */
  async searchCourses(filters: SearchFilters): Promise<Course[]> {
    try {
      const params = new URLSearchParams();

      if (filters.searchTerm) {
        params.append('searchTerm', filters.searchTerm);
      }
      if (filters.category) {
        params.append('category', filters.category);
      }
      if (filters.provider) {
        params.append('provider', filters.provider);
      }
      if (filters.courseType !== undefined) {
        params.append('courseType', String(filters.courseType));
      }
      if (filters.startDate) {
        params.append('startDate', filters.startDate);
      }
      if (filters.endDate) {
        params.append('endDate', filters.endDate);
      }

      const query = params.toString();
      const response = await this.api.get(`/api/courses/search${query ? `?${query}` : ''}`);
      return response.data;
    } catch (error) {
      console.error('Failed to search courses:', error);
      return [];
    }
  }

  /**
   * Get a specific course by ID
   */
  async getCourseById(courseId: number): Promise<Course> {
    try {
      const response = await this.api.get(`/api/courses/${courseId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch course:', error);
      throw error;
    }
  }

  /**
   * Get recommended courses for the current user from the server.
   * Returns personalized results based on registration history, falling back to trending.
   */
  async getRecommendedCourses(limit = 5): Promise<Course[]> {
    try {
      const response = await this.api.get<Course[]>(`/api/courses/recommended?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch recommended courses:', error);
      return [];
    }
  }

  /**
   * Get similar courses to a given course (client-side scoring by field-of-study overlap, level, type, credits).
   * `fieldsOfStudy` should be the source course's credit education category names
   * (see getFieldsOfStudy in utils/fieldOfStudy).
   */
  async getRelatedCourses(source: {
    id: number;
    fieldsOfStudy: string[];
    level?: string;
    courseType: CourseType;
    creditHours: number;
  }): Promise<Course[]> {
    try {
      const all = await this.getCourses();
      const sourceFos = new Set(source.fieldsOfStudy);
      return all
        .filter(c => c.id !== source.id)
        .map(c => {
          let score = 0;
          if (getFieldsOfStudy(c).some(fos => sourceFos.has(fos))) score += 4;
          if (source.level && c.level && c.level === source.level) score += 2;
          if (c.courseType === source.courseType) score += 1;
          if (Math.abs(c.creditHours - source.creditHours) <= 1) score += 1;
          return { course: c, score };
        })
        .filter(({ score }) => score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 6)
        .map(({ course }) => course);
    } catch (error) {
      console.error('Failed to fetch related courses:', error);
      return [];
    }
  }

  async reindexCourses(): Promise<{ message: string; count: number }> {
    const response = await this.api.post('/api/courses/search/reindex');
    return response.data;
  }
}

export default new CourseService();
